import React, { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/contexts/AuthContext";
import logoImg from "@/assets/nutrimarket-logo.png?w=192&format=webp";
import { safeRedirect as computeSafeRedirect } from "@/lib/safeRedirect";

export default function Auth() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "register" ? "register" : "login";
  const refFromUrl = searchParams.get("ref")?.toUpperCase() || "";
  // Where to send the user after a successful login. Only accept site-relative
  // paths on the explicit allow-list (see src/lib/safeRedirect.ts) to avoid
  // open-redirect abuse and prevent landing on unintended app routes.
  const safeRedirect = computeSafeRedirect(searchParams.get("redirect"));
  const [mode, setMode] = useState<"login" | "register">(initialMode);
  const [lang, setLang] = useState<"en" | "es">("es");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [referralCode, setReferralCode] = useState(refFromUrl);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const t = lang === "es" ? {
    welcome: "Bienvenido de vuelta",
    createAcc: "Crear cuenta",
    signInSub: "Inicia sesión en tu marketplace de bienestar",
    signUpSub: "Únete a la comunidad NutriMart",
    fullName: "Nombre Completo",
    email: "Correo Electrónico",
    password: "Contraseña",
    referral: "Código de referido (opcional)",
    referralBonus: "🎁 ¡15% de descuento en tu primera compra!",
    invalidRef: "Código de referido inválido",
    forgotPw: "¿Olvidaste tu contraseña?",
    signIn: "Iniciar Sesión",
    signUp: "Crear Cuenta",
    noAcc: "¿No tienes cuenta?",
    hasAcc: "¿Ya tienes cuenta?",
    signUpLink: "Regístrate",
    signInLink: "Inicia sesión",
    orContinue: "o continúa con",
    google: "Google",
    checkEmail: "Cuenta creada. Ya puedes entrar.",
    resetSent: "Revisa tu correo para restablecer tu contraseña.",
  } : {
    welcome: "Welcome back",
    createAcc: "Create account",
    signInSub: "Sign in to your wellness marketplace",
    signUpSub: "Join the NutriMart community",
    fullName: "Full Name",
    email: "Email",
    password: "Password",
    referral: "Referral code (optional)",
    referralBonus: "🎁 15% off your first order!",
    invalidRef: "Invalid referral code",
    forgotPw: "Forgot password?",
    signIn: "Sign In",
    signUp: "Create Account",
    noAcc: "Don't have an account?",
    hasAcc: "Already have an account?",
    signUpLink: "Sign up",
    signInLink: "Sign in",
    orContinue: "or continue with",
    google: "Google",
    checkEmail: "Account created. You can sign in now.",
    resetSent: "Check your email to reset your password.",
  };

  if (loading) return null;
  if (user) return <Navigate to={safeRedirect} replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      if (mode === "register") {
        // Validate referral code BEFORE signup
        let referrerId: string | null = null;
        const cleanCode = referralCode.trim().toUpperCase();
        if (cleanCode) {
          const { data: validation, error: valErr } = await supabase
            .rpc("validate_referral_code", { _code: cleanCode })
            .maybeSingle();
          if (valErr || !validation?.valid || !validation?.referrer_id) {
            setError(t.invalidRef);
            setSubmitting(false);
            return;
          }
          referrerId = validation.referrer_id;
        }

        const names = fullName.trim().split(" ");
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              first_name: names[0] || "",
              last_name: names.slice(1).join(" ") || "",
            },
          },
        });
        if (error) throw error;

        // Register referral link if applicable
        if (referrerId && data.user) {
          await supabase.from("referrals").insert({
            referrer_id: referrerId,
            referred_user_id: data.user.id,
            referral_code: cleanCode,
            status: "pending",
          });
          await supabase.from("profiles").update({ referred_by: referrerId }).eq("user_id", data.user.id);
        }

        // Send welcome email (fire-and-forget, idempotent by user.id)
        if (data.user) {
          // Try to fetch the referral code created by trigger (may not exist yet on first try)
          let referralCode: string | undefined;
          try {
            const { data: refRow } = await supabase
              .from("referral_codes")
              .select("code")
              .eq("user_id", data.user.id)
              .maybeSingle();
            referralCode = refRow?.code;
          } catch { /* ignore */ }

          supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "welcome",
              recipientEmail: email,
              idempotencyKey: `welcome-${data.user.id}`,
              templateData: {
                name: names[0] || undefined,
                referralCode,
              },
            },
          }).catch((e) => console.error("welcome email error:", e));
        }

        if (!data.session) setInfo(t.checkEmail);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err: any) {
      setError(err.message || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async () => {
    if (!email) { setError(lang === "es" ? "Ingresa tu correo primero" : "Enter your email first"); return; }
    setError("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) { setError(error.message); return; }
    setInfo(t.resetSent);
  };

  const handleGoogle = async () => {
    setError("");
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}${safeRedirect}`,
    });
    if (result.error) {
      setError(result.error.message || "Google sign-in failed");
    }
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg,#3D5A1E,hsl(var(--primary)) 40%,hsl(var(--primary)) 70%,#AED581 95%)",
      padding: 20, position: "relative", overflow: "hidden", fontFamily: "'Nunito Sans',system-ui,sans-serif",
    }}>
      <div style={{
        background: "#fff", borderRadius: 24, padding: "40px 32px", width: "100%", maxWidth: 400,
        boxShadow: "0 12px 40px rgba(0,0,0,.15)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Space Grotesk',system-ui,sans-serif", fontSize: 19, fontWeight: 700 }}>
            <img src={logoImg} alt="NutriMarket" width={30} height={30} style={{ objectFit: "contain" }} />
            Nutri<span style={{ color: "hsl(var(--primary))" }}>Market</span>
          </div>
          <button onClick={() => setLang(l => l === "en" ? "es" : "en")} style={{
            padding: "5px 11px", fontSize: 11, borderRadius: 12, background: "#FFF5EB", color: "#1A1A18",
            border: "1px solid #E8E2D8", cursor: "pointer", fontWeight: 600,
          }}>
            {lang === "en" ? "ES 🇩🇴" : "EN 🇺🇸"}
          </button>
        </div>

        <h1 style={{ fontFamily: "'Fraunces',Georgia,serif", fontSize: 24, fontWeight: 700, marginBottom: 3 }}>
          {mode === "login" ? t.welcome : t.createAcc}
        </h1>
        <p style={{ color: "#5A5A56", fontSize: 12.5, marginBottom: 22 }}>
          {mode === "login" ? t.signInSub : t.signUpSub}
        </p>

        {error && <div style={{ background: "#FEE2E2", color: "#DC2626", padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{error}</div>}
        {info && <div style={{ background: "#D1FAE5", color: "#065F46", padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{info}</div>}

        <form onSubmit={handleSubmit}>
          {mode === "register" && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 10.5, fontWeight: 600, color: "#5A5A56", marginBottom: 4, textTransform: "uppercase", letterSpacing: .5 }}>{t.fullName}</label>
              <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Carlos Méndez" required
                style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E8E2D8", borderRadius: 8, fontSize: 13, outline: "none" }} />
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 10.5, fontWeight: 600, color: "#5A5A56", marginBottom: 4, textTransform: "uppercase", letterSpacing: .5 }}>{t.email}</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" required
              style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E8E2D8", borderRadius: 8, fontSize: 13, outline: "none" }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 10.5, fontWeight: 600, color: "#5A5A56", marginBottom: 4, textTransform: "uppercase", letterSpacing: .5 }}>{t.password}</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={6}
              style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E8E2D8", borderRadius: 8, fontSize: 13, outline: "none" }} />
          </div>
          {mode === "register" && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 10.5, fontWeight: 600, color: "#5A5A56", marginBottom: 4, textTransform: "uppercase", letterSpacing: .5 }}>{t.referral}</label>
              <input value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} placeholder="ABCD1234" maxLength={8}
                style={{ width: "100%", padding: "9px 12px", border: referralCode ? "1.5px solid hsl(var(--primary))" : "1.5px solid #E8E2D8", borderRadius: 8, fontSize: 13, outline: "none", textTransform: "uppercase", letterSpacing: 1, fontFamily: "monospace" }} />
              {referralCode && <div style={{ fontSize: 11, color: "hsl(var(--primary))", marginTop: 4, fontWeight: 600 }}>{t.referralBonus}</div>}
            </div>
          )}
          {mode === "login" && (
            <div style={{ textAlign: "right", marginBottom: 12 }}>
              <button type="button" onClick={handleForgot} style={{ color: "hsl(var(--primary))", cursor: "pointer", fontSize: 12, fontWeight: 500, background: "none", border: "none" }}>{t.forgotPw}</button>
            </div>
          )}
          <button type="submit" disabled={submitting} style={{
            width: "100%", padding: "10px 20px", borderRadius: 12, fontSize: 13, fontWeight: 600,
            cursor: "pointer", border: "none", color: "#fff", marginBottom: 10,
            background: "linear-gradient(135deg,hsl(var(--primary) / .88),hsl(var(--primary)))",
            boxShadow: "0 2px 8px hsl(var(--primary) / .3)", opacity: submitting ? .7 : 1,
          }}>
            {submitting ? "..." : (mode === "login" ? t.signIn : t.signUp)} →
          </button>
        </form>

        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0" }}>
          <div style={{ flex: 1, height: 1, background: "#E8E2D8" }} />
          <span style={{ fontSize: 11, color: "#9C9C96" }}>{t.orContinue}</span>
          <div style={{ flex: 1, height: 1, background: "#E8E2D8" }} />
        </div>

        <button onClick={handleGoogle} style={{
          width: "100%", padding: "10px 20px", borderRadius: 12, fontSize: 13, fontWeight: 600,
          cursor: "pointer", border: "1.5px solid #E8E2D8", background: "#fff", color: "#1A1A18",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.43 3.44 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          {t.google}
        </button>

        <div style={{ textAlign: "center", fontSize: 12, color: "#5A5A56" }}>
          {mode === "login" ? t.noAcc : t.hasAcc}{" "}
          <button onClick={() => { setMode(m => m === "login" ? "register" : "login"); setError(""); setInfo(""); }}
            style={{ color: "hsl(var(--primary))", cursor: "pointer", fontSize: 12, fontWeight: 500, background: "none", border: "none" }}>
            {mode === "login" ? t.signUpLink : t.signInLink}
          </button>
        </div>
      </div>
    </div>
  );
}
